import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Col,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import { useIndicator } from '../../../hook/use_indicator';
import { getCompany, useSessionStore } from '../../../context/session';
import { RowValue } from '../../../lib/spreadsheet/types';
import { SpreadSheet } from '../../../lib/spreadsheet/spreadsheet';
import {
  BusinessTypeKeys,
  CsvBusinessTypes, CsvListDataType, DataTypeKeys, dataTypeToBusinessType,
  DateType,
  DateTypeKeys,
  getEnumByKey,
  SourceDisplayForCleaning,
  SourceDisplayForGreening,
  SourceKeys,
} from '../../../constant';
import { CsvUploadRequest } from '../../../model/csvupload_request';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';
import { useTaskCsvStore } from './task_csv_store';
import { Error, Header } from '../../../model/spread_sheet_response';
import { cssList } from '../../../utils/csslist';
import { PermissionName } from '../../../valueobject/permissionName';

/**
 * 作業計画一括投入画面
 * @constructor
 */
export const TaskCSVRegistPage:React.FC = () => {
  const navigator = useNavigate();
  // Form入力値の状態管理
  const [param, setParam] = useState<CsvUploadRequest>();
  // ファイル選択フラグ
  const [isFile, setIsFile] = useState(false);
  // 検証成否フラグ
  const [isDryRunSuccess, setIsDryRunSuccess] = useState(false);
  // ファイルアップロードボタンUI
  const { Dragger } = Upload;
  // 対象ファイルの状態管理
  const [dataTypeOptions, setDataTypeOptions] = useState<{ value: SourceKeys; label: string }[]>(SourceDisplayForCleaning);
  // インジケータ
  const indicator = useIndicator();
  // ログイン情報
  const { loginInfo, hasPermission } = useSessionStore((selector) => selector);
  // ストア情報
  const { registeTaskCsv } = useTaskCsvStore((selector) => selector);
  // プレビューエリア：ヘッダーキー
  const [header, setHeader] = useState<Header[]>([]);
  // プレビューエリア：ボディ
  const [data, setData] = useState<RowValue[]>([]);
  // プレビューエリア：ToolTip
  const [tips, setTips] = useState<string>();
  // 件数
  const [count, setCount] = useState<number>(0);
  // ファイルフォーマットエラー
  const [fileError, setFileError] = useState<Error[]>([]);
  // 完了メッセージ
  const [messageApi, contextHolder] = message.useMessage();
  // アップロードファイル管理
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [modal, showModal] = useState(false);
  const [yearLabel, setYearLabel] = useState('');

  const [height, setHeight] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [isSearchBox, setIsSearchBox] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const handleShowSearchButton = useCallback(() => {
    setIsSearchBox(!isSearchBox);
    setHeight(isSearchBox ? 40 : originalHeight);
  }, [isSearchBox]);
  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
      setOriginalHeight(ref.current.scrollHeight);
    }
  }, []);
  const style = useMemo(() => {
    if (height === 0) {
      return {};
    }
    return { height };
  }, [height]);

  /** 対象業務デフォルト値 */
  const defaultBusinessType = () => {
    return (
      loginInfo.user.company.businessType === BusinessTypeKeys.all
        ? Object.values(CsvBusinessTypes)[0].key
        : getEnumByKey(BusinessTypeKeys, loginInfo.user.company.businessType)
    );
  };

  const nowyear = useMemo(() => {
    const now = dayjs();
    if (now.month() < 4) {
      return now.year() - 1;
    }
    return now.year();
  }, []);

  useEffect(() => {
    if (param?.dateType === DateTypeKeys.yearly) {
      setYearLabel('対象年度');
      setParam((param) => ({ ...param, year: nowyear }));
    } else {
      setYearLabel('対象年');
      setParam((param) => ({ ...param, year: dayjs().year() }));
    }
  }, [param?.dateType]);

  useEffect(() => {
    const businessType = defaultBusinessType();
    if (businessType === BusinessTypeKeys.cleaning) {
      setDataTypeOptions(SourceDisplayForCleaning);
    } else {
      setDataTypeOptions(SourceDisplayForGreening);
    }
    const dataType = businessType === BusinessTypeKeys.cleaning ? SourceKeys.cleaning_daily : SourceKeys.greening_daily;
    // 初期値設定
    setParam((param) => {
      return {
        ...param,
        companyId: getCompany(loginInfo).id,
        businessType,
        dataType,
        dateType: DateTypeKeys.monthly,
        year: nowyear,
        month: dayjs().month() + 1,
      };
    });
  }, []);

  /** 対象業務 */
  const csvTypeOptions = useMemo(() => {
    return [
      ...CsvBusinessTypes.filter((type) => {
        return hasPermission(PermissionName.ALL_WORK_PLAN, true) || type.key === loginInfo?.user?.company?.businessType;
      }).map((type) => {
        return { value: type.key, label: type.value };
      }),
    ];
  }, []);

  /** 対象業務プルダウン変更時 */
  const handleBusinessType = (value:BusinessTypeKeys) => {
    setParam((param) => {
      if (value !== param.businessType) {
        const dataType = value === BusinessTypeKeys.cleaning ? SourceKeys.cleaning_daily : SourceKeys.greening_daily;
        return { ...param, dataType, businessType: value };
      }
      return { ...param, businessType: value };
    });

    // 対象ファイルを変更
    if (value === BusinessTypeKeys.cleaning) {
      setDataTypeOptions(SourceDisplayForCleaning);
    } else {
      setDataTypeOptions(SourceDisplayForGreening);
    }
  };

  /** 年次 */
  const now = dayjs();
  const yearOptions = [
    ...Array.from({ length: 6 }, (_, i) => {
      return { value: `${now.year() - (i - 1)}`, label: `${now.year() - (i - 1)}` };
    }),
  ];

  /** 月次 */
  const monthOptions = [
    ...Array.from({ length: 12 }, (_, i) => {
      return { value: `${i + 1}`, label: `${i + 1}` };
    }),
  ];

  /** 対象期間プルダウン変更時 */
  const handleDateType = (value:DateTypeKeys) => {
    setParam((param) => {
      return { ...param, dateType: value };
    });
  };

  /** ファイルアップロードProps */
  const fileprops: UploadProps = {
    accept: '.csv,.CSV',
    beforeUpload: (file) => {
      const binaryData = new Blob([file]);
      setParam((param) => {
        return { ...param, file: binaryData } as CsvUploadRequest;
      });

      // ファイルリストに設定
      setFileList([file]);

      // フラグ初期化
      setIsFile(true);
      setIsDryRunSuccess(false);
      setFileError([]);

      // Antdの自動アップロードを抑止
      return false;
    },
    multiple: false,
    onRemove: () => {
      setFileList([]);
      setIsFile(false);
      setIsDryRunSuccess(false);
      setFileError([]);
    },
    fileList, // 選択したファイルを管理
  };

  /** プレビュー */
  const handlePreview = () => {
    indicator.show();
    setIsFile(false);
    registeTaskCsv(param, true).then((res) => {

      if (res.errors.length !== 0) {
        // ファイルフォーマットエラー
        setFileError(res.errors);
        return;
      }
      setCount(res.data.count);

      setHeader(res.data.headers);
      // ボディ設定
      setData(res.data.rows);
      // ツールチップ設定
      setTips(res.data.tips);
      // プレビュー
      setIsDryRunSuccess(true);

    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else if (status === HttpStatusCode.UnsupportedMediaType) {
          window.alert(Messages.ERR_UNSUPPORTED_CSV);
          setFileList([]);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  /** エラーメッセージ */
  const ErrorInfos = () => {
    return (
      <div>
        {fileError
          // .slice() // 元のstateを直接変更しないようコピー
          .sort((a, b) => a.row - b.row)
          .map((error, index) => (
            <div key={index}>
              {error.row}行目：{error.message}
            </div>
          ))}
      </div>
    );
  };

  const isPreview = useMemo(() => {
    return isDryRunSuccess && fileError.length === 0;
  }, [isDryRunSuccess, fileError?.length]);

  /** 登録 */
  const handleRegister = useCallback(() => {
    showModal(false);
    indicator.show();
    registeTaskCsv(param).then(() => {
      // 完了メッセージ
      messageApi.success(<>{Messages.EXECUTE_CSV_REGISTER1}<br/>{Messages.EXECUTE_CSV_REGISTER2}</>);

      // ファイルリスト初期化
      setFileList([]);
      // フラグ初期化
      setIsFile(false);
      setIsDryRunSuccess(false);
      // 検索項目初期化

      const businessType = defaultBusinessType();
      if (businessType === BusinessTypeKeys.cleaning) {
        setDataTypeOptions(SourceDisplayForCleaning);
      } else {
        setDataTypeOptions(SourceDisplayForGreening);
      }
      const dataType = businessType === BusinessTypeKeys.cleaning ? SourceKeys.cleaning_daily : SourceKeys.greening_daily;
      // 初期値設定
      setParam((param) => {
        return {
          ...param,
          companyId: getCompany(loginInfo).id,
          businessType,
          dataType,
          dateType: DateTypeKeys.monthly,
          year: dayjs().year(),
          month: dayjs().month() + 1,
        };
      });

    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === HttpStatusCode.UnsupportedMediaType) {
          window.alert(Messages.ERR_UNSUPPORTED_CSV);
          setFileList([]);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  }, [loginInfo, indicator, param, navigator]);

  useEffect(() => {
    if (param?.dataType === SourceKeys.cleaning_daily || param?.dataType === SourceKeys.greening_daily) {
      setParam((param) => {
        return { ...param, dateType: DateTypeKeys.monthly };
      });
    } else {
      setParam((param) => {
        return { ...param, dateType: DateTypeKeys.yearly };
      });
    }
  }, [param?.dataType]);

  const tooltip = useMemo(() => {
    if (tips === undefined) {
      return null;
    }
    const t = tips.split('\n');
    return (
      <>
        {t.map((tip, index) => (
          <div key={index}>{tip}</div>
        ))}
      </>
    );
  }, [tips]);

  return (
    <div>
      <h1>作業計画一括投入</h1>
      <Modal
          title={'確認'}
          open={modal}
          onOk={handleRegister}
          onCancel={() => showModal(false)}
      >
        <p>
          作業計画一括投入に要する時間は最遅で 5行/分 程度となります。<br/>
          登録してよろしいでしょうか？
        </p>
      </Modal>
      <div
        ref={ref}
        style={style}
        className={cssList(styles.section, styles.relative, styles.searchBox, {
          [styles.hide]: !isSearchBox,
        })}
      >
          <span className={styles.menuShowBtn}>
            <Button
              type={'dashed'}
              size={'small'}
              onClick={handleShowSearchButton}
              style={{ color: 'gray' }}
            >
              {isSearchBox ?? true ? '検索条件 非表示' : '検索条件 表示'}
            </Button>
          </span>
        <div className={styles.searchBox + ' ' + (isSearchBox ? '' : styles.hide)}>
          {contextHolder}
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              担当会社
            </div>
            <Col span={8}>
              {getCompany(loginInfo).companyName}
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              対象業務
            </div>
            <Col span={4}>
              <Select
                disabled={isPreview}
                style={{ width: 120 }}
                value={param?.businessType ?? defaultBusinessType()}
                options={csvTypeOptions}
                onChange={handleBusinessType}
              />
            </Col>
            <div className={styles.formLabel}>
              対象ファイル
            </div>
            <Col>
              <Select
                disabled={isPreview}
                style={{ width: 280 }}
                value={param?.dataType ?? dataTypeOptions[0].value}
                options={dataTypeOptions.map((v) => ({ value: v.value, label: v.label }))}
                onChange={(value) => setParam((param) => {
                  return { ...param, dataType: value } as CsvUploadRequest;
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              {yearLabel}
            </div>
            <Col span={4}>
              <Select
                disabled={isPreview}
                style={{ width: 120 }}
                value={param?.year ?? nowyear}
                options={yearOptions}
                onChange={(value) => setParam((param) => {
                  return { ...param, year: value };
                })}
              />
            </Col>
            <div className={styles.formLabel}>
              対象期間
            </div>
            <Col>
              <Space>
                <Radio.Group
                  disabled={isPreview}
                  value={param?.dateType ?? DateTypeKeys.yearly}
                  onChange={(e) => handleDateType(e.target.value)}
                >
                  {DateType.map((type) => (
                    <Radio key={type.key} value={type.key} disabled={(type.key !== param?.dateType)}>
                      {type.value}
                    </Radio>
                  ))}
                </Radio.Group>
                <Select
                  disabled={isPreview || param?.dateType !== DateTypeKeys.monthly}
                  style={{ width: 120 }}
                  value={param?.month ?? dayjs().month() + 1}
                  options={monthOptions}
                  onChange={(value) => setParam((param) => {
                    return { ...param, month: value };
                  })}
                />
              </Space>
            </Col>
          </Row>
          <div className='form'>
            アップロードファイル
            <div className={styles.upload}>
              <Dragger {...fileprops}
                 maxCount={1}
              >
                <InboxOutlined/>&nbsp;&nbsp;ファイル選択
              </Dragger>
            </div>
          </div>
          <br/><br/>
          <Row className={styles.row}>
            <Space>
              <Button
                disabled={!isFile}
                loading={indicator.showing && !isDryRunSuccess}
                type={'primary'}
                onClick={handlePreview}
              >
                プレビュー
              </Button>
              <Button disabled={!isDryRunSuccess || fileError.length !== 0}
                      loading={indicator.showing && isDryRunSuccess} type={'primary'}
                      onClick={() => showModal(true) }>登録</Button>
            </Space>
          </Row>
        </div>
      </div>

      {(fileError.length !== 0)
        && <div className={styles.section + ' ' + styles.error}>
          <ErrorInfos/>
          </div>
      }
      {(isDryRunSuccess && fileError.length === 0)
        && <div className='section_no_backcolor'>
              <div className={styles.editarea + (isDryRunSuccess ? ' ' + styles.show : '')}>
                  <div>
                      <SpreadSheet
                          historyId={0}
                          count={count}
                          targetFile={param?.dataType ?? SourceKeys.cleaning_daily}
                          dateType={param?.dateType ?? DateTypeKeys.yearly}
                          year={param?.year ?? nowyear}
                          month={param?.month ?? dayjs().month() + 1}
                          headers={header}
                          toolTip={tooltip}
                          showId={false}
                          freezeX={8}
                          parPage={50}
                          lastModified={dayjs()}
                          editable={false}
                          resultCaption={'投入する行数'}
                          initialRows={data}/>
                  </div>
              </div>
          </div>
      }
    </div>
  );
};
